import { ViewData } from '@/endpoints/schemas'
import { GeneralNodeActions } from '@/store/modules/tab/types'
import { updateTabData } from '@/store/utils'
import { NativeMap } from '@/utils/collections'

import { ensureValidData, getStructureSerializedData } from '../helpers'
import { TAB_CLOSE } from '../tab/constants'
import { flatTablePermission } from '../table/helpers'
import { Actions } from './actions'
import { VIEW_INIT, VIEW_SAVE, VIEW_SELECT_TAB, VIEW_UPDATE } from './constants'
import { defaultViewData, getInitialViewData } from './helpers'
import { OpenedViewData, ViewPermissionFlat, ViewTab } from './types'

type State = Readonly<typeof initialState>

const initialState = {
	views: {} as NativeMap<OpenedViewData>,
}

export default (
	state = initialState,
	action: Actions | GeneralNodeActions,
): State => {
	switch (action.type) {
		case TAB_CLOSE: {
			const views = { ...state.views }
			delete views[action.nodeId]

			return {
				...state,
				views,
			}
		}

		case VIEW_INIT: {
			const { node, editMode, force, refreshWorkingCopy } = action

			const previous = state.views[node.id]

			if (previous && editMode && previous.parsedEditMode && !force) {
				return state
			}

			const serializedData = getStructureSerializedData(
				node,
				editMode,
				refreshWorkingCopy,
			)

			// TODO: we should validate data against the JSON schema
			const parsed = JSON.parse(serializedData || '{}')

			const data: ViewData = Object.keys(parsed).length
				? ensureValidData(parsed, defaultViewData)
				: getInitialViewData(node.name)

			if (!data.columns) {
				data.columns = []
			}

			const ids = data.columns.map((c) => c.id)
			data.columnsLastId = Math.max(0, ...ids)

			const objectPermissions =
				data.objectPermissions
					?.filter((x) => x.user)
					.map(flatTablePermission) ?? []

			data.objectPermissions = objectPermissions as ViewPermissionFlat[]

			if (typeof data.objectPermissionLastId !== 'number') {
				data.objectPermissionLastId = Math.max(
					0,
					...(data.objectPermissions?.map((c) => c.id ?? 0) ?? [0]),
				)
			}

			return {
				...state,
				views: {
					...state.views,
					[node.id]: {
						form: data,
						original: data,
						tab: previous ? previous.tab : ViewTab.Overview,
						dirty: false,
						parsedEditMode: editMode,
					} as OpenedViewData,
				},
			}
		}

		case VIEW_SAVE: {
			const { node } = action.metadata

			return {
				...state,
				views: updateTabData(state.views, node.id, (node) => ({
					...node,
					dirty: false,
				})),
			}
		}

		case VIEW_UPDATE: {
			const { node, update } = action

			return {
				...state,
				views: updateTabData(state.views, node.id, (node) => ({
					...node,
					form: {
						...node.form,
						...update,
					},
					dirty: true,
				})),
			}
		}

		case VIEW_SELECT_TAB: {
			return {
				...state,
				views: updateTabData(state.views, action.node.id, {
					tab: action.tab,
				}),
			}
		}

		default:
			return state
	}
}
